import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useSWR from 'swr';
import UserViewEnums from '@/constants/users/UserViewEnums';
import fetcher from '@/lib/swr/fetcher';
import userActionTypes from '@/rdx/actionTypes';
import type RootState from '@/types/RootState';
import { createAction } from '@/utils/rdxActions';

const useCurrentUser = () => {
  const dispatch = useDispatch();

  const {
    user,
    goals,
    visits,
    userId,
    ownerOrgId,
    isSuperAdmin,
    isImpersonating,
    views,
  } = useSelector((state: RootState) => ({
    user: state.user?.user,
    goals: state.user?.goals,
    visits: state.user?.visits,
    userId: state.authentication?.userId,
    ownerOrgId: state.authentication?.ownerOrgId,
    isSuperAdmin: state.authentication?.isSuperAdmin,
    isImpersonating: state.authentication?.isImpersonating,
    views: state.authentication?.views,
  }));

  const { data: userData, error: userError } = useSWR(
    !user?.userId &&
      userId &&
      ownerOrgId &&
      `orgs/${ownerOrgId}/users/${userId}`,
    fetcher,
  );

  React.useEffect(() => {
    // This hook uses swr to fetch the data. The app itself is still using
    // Redux to access that data in various different places. When we have that
    // data from swr, update Redux.
    if (userData) {
      dispatch(
        createAction({
          type: userActionTypes.USERS_SUCCESS,
          data: userData || {},
        }),
      );
    }

    if (userError) {
      dispatch(
        createAction({
          type: userActionTypes.USERS_FAILURE,
          err: userError,
        }),
      );
    }
  }, [user, userData, userError]);

  return {
    user,
    goals,
    visits,
    isImpersonating,
    isSuperAdmin: Boolean(isSuperAdmin),
    isOrgAdmin: views?.includes(UserViewEnums.ADMIN),
    isOrgEditor: views?.includes(UserViewEnums.EDITOR),
    views,
  };
};

export default useCurrentUser;
